<template>
  <div class="file-preview">
    <!-- 预览区：高度需扣除底部互动栏 60px，否则 PPT/PDF 会被挡住 -->
    <div ref="previewBody" class="preview-body">
      <div v-if="loading" class="preview-loading">
        <van-loading color="#000" size="24px" vertical>加载中...</van-loading>
      </div>

      <!-- type=5 腾讯云视频，能力复用 CourseMaterialPopup / TrainingCampVideoPlayer -->
      <div v-if="isVideo" class="preview-video">
        <training-camp-video-player
          v-if="playerVideo"
          ref="mediaVideoPlayer"
          :playerVideo="playerVideo"
          :enable-trace="true"
        />
      </div>

      <!-- type=6 PPT；vue-office-pptx 必须先拿到容器宽高 pptOptions.width -->
      <vue-office-pptx
        v-else-if="documentType === 'ppt' && previewUrl && pptOptions.width"
        :src="previewUrl"
        :options="pptOptions"
        class="ppt-viewer"
        @rendered="loading = false"
        @error="handlePptRenderError"
      />

      <!-- type=8 且 url 为 .pdf，用 pdfh5 渲染 -->
      <div
        v-else-if="documentType === 'pdf' && previewUrl"
        ref="pdfViewer"
        class="pdf-viewer"
      ></div>

      <!-- type=8 图片，支持双指缩放 -->
      <div
        v-else-if="documentType === 'image' && previewUrl"
        class="image-viewer"
      >
        <div
          ref="imageViewerContainer"
          class="image-viewer-container"
          :class="{ 'is-zoomed': imageScale > 1 }"
        >
          <div
            class="image-viewer-stage"
            :style="imageStageStyle"
            @touchstart="handleImageTouchStart"
            @touchmove="handleImageTouchMove"
            @touchend="handleImageTouchEnd"
            @touchcancel="handleImageTouchEnd"
          >
            <img
              :src="previewUrl"
              class="image-viewer-img"
              alt=""
              draggable="false"
              @load="handleImageLoaded"
              @error="handleImageLoaded"
            />
          </div>
        </div>
      </div>

      <div v-else-if="!loading" class="preview-empty">暂无预览内容</div>
    </div>

    <!-- 底部互动栏 -->
    <div class="preview-footer">
      <div class="footer-left">
        <div class="follow" @click="handleLike">
          <img
            v-if="!detail.isLike"
            src="@/assets/images/article/icon-case.png"
            alt=""
            class="img"
          />
          <img
            v-else
            src="@/assets/images/article/icon-case-active.png"
            alt=""
            class="img"
          />
          <span class="num">{{ detail.likeNum || 0 }}</span>
        </div>
        <div class="follow" @click="handleCollect">
          <img
            v-if="!detail.isCollect"
            src="@/assets/images/article/icon-follow.png"
            alt=""
            class="img"
          />
          <img
            v-else
            src="@/assets/images/article/icon-follow-active.png"
            alt=""
            class="img"
          />
          <span class="num">{{ detail.collectNum || 0 }}</span>
        </div>
        <div class="follow" @click="handleTranspond">
          <img
            src="@/assets/images/article/icon-transpond.png"
            alt=""
            class="img"
          />
        </div>
      </div>
    </div>

    <!-- 转发引导弹窗：提示用户点击右上角三点 -->
    <transpond
      v-if="isTranspond"
      @handlecanel="handleTranspondCancel"
    ></transpond>

    <returnBack></returnBack>
  </div>
</template>

<script>
/**
 * 课件文件预览页
 *
 * 路由：/filePreview
 * query：
 *   materialId   必填，用它查详情回显
 *   entrance / channel  入口来源
 *     163 = PFA图书馆
 *     165 = LAAC图书馆
 *     11  = 课程详情
 *     59  = 我的收藏
 *   shareId  自定义分享打开时可能带上（WHITE_PATHS_BY_SHARE），本页不走 WHITE_PATHS_BY_SHARE_INIT
 *
 * 埋点：在 pollingPath 里，走 withRouteTracking 轮询；serviceId/mainId 用 materialId
 * 分享：在 SHARE_PATH 里，由本页 getShareInfo 自定义，afterEach 不会盖成默认分享
 *
 * 素材 contentType：5 视频 / 6 PPT / 8 图文（按 url 后缀再分成 pdf / 图片）
 */
import withRouteTracking from '@/utils/withRouteTracking'
import TrainingCampVideoPlayer from '@/views/RTCZone/compontents/TrainingCampVideoPlayer.vue'
import VueOfficePptx from '@vue-office/pptx'
import Pdfh5 from 'pdfh5'
import 'pdfh5/css/pdfh5.css'
import returnBack from '@/views/person/components/returnBack.vue'
import transpond from '@/views/article/components/transpond.vue'
import {getShareInfo} from '@/utils/wxShare'
import {
  getMaterialsPreview,
  likeMaterials,
  cancelLikeMaterials,
  collectMaterials,
  cancelCollectMaterials
} from '@/api/pfa'

const IMAGE_MIN_SCALE = 1
const IMAGE_MAX_SCALE = 5
const DEFAULT_SHARE_IMAGE = 'https://iep.jjmc.cn/bw/file/material/image/1933146816339349528.jpg'

const UserProfile = {
  name: 'FilePreview',
  components: {
    TrainingCampVideoPlayer,
    VueOfficePptx,
    returnBack,
    transpond
  },
  data() {
    return {
      loading: true,
      materialId: '',
      // 接口字段：materialId / title / format / contentType / url / fileId / appId / psign / duration /
      //           coverUrl / likeNum / collectNum / isLike / isCollect
      detail: {
        materialId: '',
        title: '',
        format: '',
        contentType: '',
        url: '',
        fileId: '',
        appId: '',
        psign: '',
        duration: 0,
        coverUrl: '',
        likeNum: 0,
        collectNum: 0,
        isLike: false,
        isCollect: false
      },
      playerVideo: null,
      previewUrl: '',
      documentType: '',
      pptOptions: {},
      pdfh5: null,
      imageScale: 1,
      imageTranslateX: 0,
      imageTranslateY: 0,
      imageTouch: null,
      isTranspond: false
    }
  },
  computed: {
    isVideo() {
      return Number(this.detail.contentType) === 5
    },
    userInfo() {
      return this.$store.getters.userInfo
    },
    imageStageStyle() {
      return {
        transform: `translate3d(${this.imageTranslateX}px, ${this.imageTranslateY}px, 0) scale(${this.imageScale})`
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.updatePptOptions)
    // pollingPath 轮询埋点读 sessionStorage 的 mainId / serviceId / entrance
    this.materialId = this.$route.query.materialId || ''
    window.sessionStorage.setItem('mainId', this.materialId)
    window.sessionStorage.setItem('serviceId', this.materialId)
    const entrance = this.$route.query.entrance || window.sessionStorage.getItem('entrance') || ''
    window.sessionStorage.setItem('entrance', entrance)
    this.setVideoTrackCache()
    this.loadDetail()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updatePptOptions)
    this.destroyPdfViewer()
    // 离开时清掉，避免污染其他页面的视频埋点
    sessionStorage.removeItem('video-track-cache')
  },
  methods: {
    // handleTraceVideo 优先读 video-track-cache，避免沿用上一页的入口
    setVideoTrackCache() {
      const entrance = `${this.$route.query.entrance || window.sessionStorage.getItem('entrance') || ''}`
      const channel = `${this.$route.query.channel || entrance}`
      sessionStorage.setItem('video-track-cache', JSON.stringify({
        entrance,
        channel,
        campId: this.$route.query.businessId || ''
      }))
    },
    // 自定义分享文案跟 PFA / LAAC / 课程详情列表页保持一致
    initShareConfig() {
      const title = this.detail.title || '文件预览'
      getShareInfo({
        title,
        titleTimeLine: title,
        desc: '',
        link: window.location.href,
        linkTimeLine: window.location.href,
        images: this.detail.coverUrl || DEFAULT_SHARE_IMAGE
      })
    },
    // 课件点赞 / 取消点赞：根据当前状态调用不同接口，成功后再更新前端态
    async handleLike() {
      try {
        const userId = this.userInfo.id
        const params = {
          userId,
          type: 12,
          kind: 13,
          likeId: this.materialId,
          likeTitle: this.detail.title,
          cover: this.detail.coverUrl
        }
        const res = this.detail.isLike
          ? await cancelLikeMaterials({ userId, baseId: this.materialId, type: 12, kind: 13 })
          : await likeMaterials(params)
        if (res.code === 0 || res.code === 200) {
          this.detail.isLike = !this.detail.isLike
          this.detail.likeNum = Math.max(0, this.detail.likeNum + (this.detail.isLike ? 1 : -1))
        }
      } catch (e) {
        this.$toast(e.msg)
      }
    },
    // 课件收藏 / 取消收藏：根据当前状态调用不同接口，成功后再更新前端态
    async handleCollect() {
      try {
        const userId = this.userInfo.id
        const params = { userId, baseId: this.materialId, baseType: 9 }
        const res = this.detail.isCollect
          ? await cancelCollectMaterials(params)
          : await collectMaterials(params)
        if (res.code === 0 || res.code === 200) {
          this.detail.isCollect = !this.detail.isCollect
          this.detail.collectNum = Math.max(0, this.detail.collectNum + (this.detail.isCollect ? 1 : -1))
        }
      } catch (e) {
        this.$toast(e.msg)
      }
    },
    // 打开转发引导弹窗
    handleTranspond() {
      this.isTranspond = true
    },
    // 关闭转发引导弹窗
    handleTranspondCancel() {
      this.isTranspond = false
    },
    // 查询素材预览详情
    async loadDetail() {
      if (!this.materialId) {
        this.loading = false
        this.$toast('文件信息缺失')
        return
      }
      this.loading = true
      try {
        const res = await getMaterialsPreview({ materialId: this.materialId })
        if (res.code === 0 && res.data) {
          this.detail = res.data || {}
          this.initShareConfig()
          this.renderPreview()
        }
      } catch (e) {
        this.loading = false
        this.$toast(e.msg)
      }
    },
    // 按 contentType + url 后缀分流：视频 / pdf / 图片 / ppt
    renderPreview() {
      this.destroyPdfViewer()
      this.resetImageTransform()
      this.playerVideo = null
      this.previewUrl = ''
      this.documentType = ''
      // 视频
      if (this.isVideo) {
        this.playerVideo = {
          id: this.detail.materialId,
          fileId: this.detail.fileId,
          appId: this.detail.appId,
          psign: this.detail.psign,
          coverUrl: this.detail.coverUrl,
          title: this.detail.title,
          videoDuration: this.detail.duration
        }
        this.loading = false
        return
      }
      const url = this.resolveMaterialPreviewUrl(this.detail.url)
      // pdf
      if (this.isPdfUrl(url)) {
        this.documentType = 'pdf'
        this.previewUrl = url
        this.$nextTick(() => {
          this.updatePptOptions()
          this.loading = false
          this.initPdfViewer()
        })
        return
      }
      // 图片
      if (this.isImageUrl(url)) {
        this.documentType = 'image'
        this.previewUrl = url
        this.loading = false
        return
      }
      // ppt
      this.documentType = 'ppt'
      this.previewUrl = url
      this.$nextTick(this.updatePptOptions)
    },
    updatePptOptions() {
      const el = this.$refs.previewBody
      const width = el && el.clientWidth ? el.clientWidth : (window.innerWidth || 0)
      const height = el && el.clientHeight ? el.clientHeight : (window.innerHeight || 0)
      this.pptOptions = {
        width: Math.round(width),
        height: Math.round(height)
      }
    },
    isPdfUrl(url) {
      if (!url) return false
      const cleanUrl = `${url}`.split('?')[0].split('#')[0]
      return /\.pdf$/i.test(cleanUrl)
    },
    isImageUrl(url) {
      if (!url) return false
      const cleanUrl = `${url}`.split('?')[0].split('#')[0]
      return /\.(png|jpe?g|gif|webp)$/i.test(cleanUrl)
    },
    resolveMaterialPreviewUrl(url) {
      if (!url) return url
      const urlStr = `${url}`.trim()
      if (process.env.NODE_ENV !== 'development') return urlStr
      if (urlStr.startsWith('/bw/')) return urlStr
      const bwIndex = urlStr.indexOf('/bw/')
      if (bwIndex > -1) return urlStr.slice(bwIndex)
      return urlStr
    },
    initPdfViewer() {
      if (this.documentType !== 'pdf' || !this.previewUrl || !this.$refs.pdfViewer || this.pdfh5) return
      try {
        this.pdfh5 = new Pdfh5(this.$refs.pdfViewer, {
          pdfurl: this.previewUrl
        })
        this.pdfh5.on('error', () => {
          this.$toast('PDF 加载失败，请稍后重试')
        })
      } catch (err) {
        this.loading = false
      }
    },
    destroyPdfViewer() {
      if (this.pdfh5 && this.pdfh5.destroy) {
        this.pdfh5.destroy()
      }
      this.pdfh5 = null
    },
    handlePptRenderError() {
      this.loading = false
      this.$toast('文件加载失败，请稍后重试')
    },
    handleImageLoaded() {
      this.loading = false
    },
    resetImageTransform() {
      this.imageScale = 1
      this.imageTranslateX = 0
      this.imageTranslateY = 0
      this.imageTouch = null
      const container = this.$refs.imageViewerContainer
      if (container) {
        container.scrollLeft = 0
        container.scrollTop = 0
      }
    },
    getTouchDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX
      const dy = touches[0].clientY - touches[1].clientY
      return Math.hypot(dx, dy)
    },
    getTouchCenter(touches) {
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2
      }
    },
    handleImageTouchStart(event) {
      if (this.documentType !== 'image') return
      const touches = event.touches
      if (touches.length === 2) {
        const container = this.$refs.imageViewerContainer
        const center = this.getTouchCenter(touches)
        let startFocalX = center.x
        let startFocalY = center.y
        if (container) {
          const rect = container.getBoundingClientRect()
          startFocalX = center.x - rect.left + container.scrollLeft
          startFocalY = center.y - rect.top + container.scrollTop
        }
        this.imageTouch = {
          mode: 'pinch',
          startDistance: this.getTouchDistance(touches),
          startScale: this.imageScale,
          startTranslateX: this.imageTranslateX,
          startTranslateY: this.imageTranslateY,
          startFocalX,
          startFocalY
        }
        return
      }
      if (touches.length === 1 && this.imageScale > 1) {
        this.imageTouch = {
          mode: 'pan',
          startX: touches[0].clientX,
          startY: touches[0].clientY,
          startTranslateX: this.imageTranslateX,
          startTranslateY: this.imageTranslateY
        }
      }
    },
    handleImageTouchMove(event) {
      if (!this.imageTouch || this.documentType !== 'image') return
      const touches = event.touches
      if (this.imageTouch.mode === 'pinch' && touches.length >= 2) {
        event.preventDefault()
        const distance = this.getTouchDistance(touches)
        if (!this.imageTouch.startDistance) return
        let nextScale = this.imageTouch.startScale * (distance / this.imageTouch.startDistance)
        nextScale = Math.min(IMAGE_MAX_SCALE, Math.max(IMAGE_MIN_SCALE, nextScale))
        const scaleRatio = nextScale / this.imageTouch.startScale
        const {startFocalX, startFocalY, startTranslateX, startTranslateY} = this.imageTouch
        this.imageTranslateX = startTranslateX + (startFocalX - startTranslateX) * (1 - scaleRatio)
        this.imageTranslateY = startTranslateY + (startFocalY - startTranslateY) * (1 - scaleRatio)
        this.imageScale = nextScale
        if (nextScale <= 1) {
          this.resetImageTransform()
        }
        return
      }
      if (this.imageTouch.mode === 'pan' && touches.length === 1 && this.imageScale > 1) {
        event.preventDefault()
        const dx = touches[0].clientX - this.imageTouch.startX
        const dy = touches[0].clientY - this.imageTouch.startY
        this.imageTranslateX = this.imageTouch.startTranslateX + dx
        this.imageTranslateY = this.imageTouch.startTranslateY + dy
      }
    },
    handleImageTouchEnd(event) {
      if (!this.imageTouch) return
      const touches = event.touches
      if (!touches.length) {
        if (this.imageScale <= 1) {
          this.resetImageTransform()
        }
        this.imageTouch = null
        return
      }
      if (touches.length === 1 && this.imageTouch.mode === 'pinch' && this.imageScale > 1) {
        this.imageTouch = {
          mode: 'pan',
          startX: touches[0].clientX,
          startY: touches[0].clientY,
          startTranslateX: this.imageTranslateX,
          startTranslateY: this.imageTranslateY
        }
      }
    }
  }
}
export default withRouteTracking(UserProfile)
</script>

<style lang="less" scoped>
.file-preview {
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

.preview-body {
  position: relative;
  width: 100%;
  height: calc(100vh - 60px); // 底部互动栏高度
  overflow: hidden;
}

.preview-loading {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 9;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  /deep/ .van-loading__text {
    color: #fff;
  }
}

.preview-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #fff;
  font-size: 14px;
}

.preview-video {
  width: 100%;

  /deep/ .player,
  /deep/ .video-player {
    width: 100%;
  }
}

.ppt-viewer,
.pdf-viewer,
.image-viewer {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.ppt-viewer {
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.pdf-viewer {
  background: #f5f5f5;
}

.image-viewer-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: auto;
  -webkit-overflow-scrolling: touch;

  &.is-zoomed {
    overflow: hidden;
    touch-action: none;
  }
}

.image-viewer-stage {
  transform-origin: 0 0;
  will-change: transform;
}

.image-viewer-img {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  user-select: none;
  -webkit-user-drag: none;
}

.preview-footer {
  display: flex;
  align-items: center;
  position: fixed;
  z-index: 99;
  bottom: 0;
  left: 0;
  height: 60px;
  padding: 12px 12px 15px;
  box-sizing: border-box;
  width: 100%;
  background: #fff;

  .footer-left {
    display: flex;

    .follow {
      flex: 1;
      display: flex;
      align-items: center;
      margin: 0 10px;

      .img {
        width: 22px;
        height: 22px;
        margin-right: 3px;
      }

      .num {
        font-weight: 500;
        font-size: 14px;
        color: #323232;
        line-height: 20px;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/pfa/filePreview.vue